import { useCallback, useEffect, useRef } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Prompt, useHistory } from 'react-router-dom'
import { useConfirmDialog } from 'store/actions/ui.action'
import { useAppSelector } from 'store/useStoreHook'

function useUnsavedWarning(
    isFormDirty: boolean,
    message: string = 'unsaved_warning',
) {
    const { t } = useTranslation()
    const [isDirty, setIsDirty] = useState(false)
    const [isConfirm, setIsConfirm] = useState<boolean>(false)
    const { showConfirmDialog } = useConfirmDialog()
    const isAuthExpired = useAppSelector((s) => s.auth.isAuthExpired)
    const nextRouteRef = useRef<any>(null)
    const history = useHistory()
    /**
     * Force is dirty to false when user token was expired
     */
    useEffect(() => {
        if (isAuthExpired) {
            if (isDirty) {
                setIsDirty(false)
            }
        }
        // window.onbeforeunload = null
    }, [isAuthExpired, isDirty])

    useEffect(() => {
        if (isDirty) {
            //Detecting browser closing
            window.onbeforeunload = () => {
                return t(message)
            }
            return () => {
                window.onbeforeunload = null
            }
        } else {
            window.onbeforeunload = null
        }
    }, [isDirty, message, t])

    useEffect(() => {
        if (isConfirm && !isDirty) {
            history.push(nextRouteRef.current.pathname)
            return () => {
                setIsConfirm(false)
            }
        }
    }, [isConfirm, history, isDirty])

    const routerPrompt = (
        <Prompt
            message={(nextLo) => {
                if (
                    history.location.pathname !== nextLo.pathname &&
                    isDirty &&
                    !isAuthExpired
                ) {
                    nextRouteRef.current = nextLo
                    showConfirmDialog(
                        t(message),
                        (result) => {
                            if (result) {
                                setIsDirty(false)
                                setIsConfirm(true)
                            } else {
                                nextRouteRef.current = null
                            }
                        },
                        'Leave page?',
                        { discardBtnText: 'Cancel' },
                    )
                    return false
                } else {
                    return true
                }
            }}
        />
    )

    const setDirty = useCallback(() => {
        if (!isAuthExpired) {
            setIsDirty(true)
        }
    }, [isAuthExpired])

    const setPristine = useCallback(() => {
        setIsDirty(false)
    }, [])

    useEffect(() => {
        if (isFormDirty) {
            setDirty()
        } else {
            setPristine()
        }
    }, [isFormDirty, setDirty, setPristine])

    return [routerPrompt, setPristine] as const
}

export default useUnsavedWarning
